import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Type, Loader2, ArrowLeft, Layers } from 'lucide-react';
import api from "../../../../api/axios";

const EditSousCategory = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [name, setName] = useState('');
    const [parentId, setParentId] = useState('');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // 1. Fetch all categories (the tree structure)
                const catsRes = await api.get("/categories");
                const allData = catsRes.data.data;
                
                // Set the dropdown list
                setCategories(allData);

                // 2. Recursive function to find the sub-category anywhere in the tree
                const findCategoryById = (data, targetId) => {
                    for (let item of data) {
                        if (Number(item.id) === Number(targetId)) {
                            return item;
                        }
                        if (item.children && item.children.length > 0) {
                            const found = findCategoryById(item.children, targetId);
                            if (found) return found;
                        }
                    }
                    return null;
                };

                const categoryToEdit = findCategoryById(allData, id);

                // 3. Populate fields
                if (categoryToEdit) {
                    setName(categoryToEdit.name);
                    // IMPORTANT: Convert to string for the <select> to recognize it
                    setParentId(categoryToEdit.parent_id ? String(categoryToEdit.parent_id) : '');
                }

            } catch (err) {
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await api.put(`/sous-categories/${id}`, {
                name: name,
                parent_id: parentId
            });
            navigate('/admin/dashboard/categories');
        } catch (error) {
            console.error("Update error:", error);
            alert("Erreur lors de la modification.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-40">
                <Loader2 className="animate-spin text-[#ffaa00] mb-4" size={40} />
                <p className="text-xs font-black uppercase tracking-widest text-gray-400">Chargement...</p>
            </div>
        );
    }

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto p-6">
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-[#8A8A89] font-bold text-xs mb-8 uppercase hover:text-black transition-all"
            >
                <ArrowLeft size={16} /> Retour
            </button>

            <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 md:p-12 border border-gray-50">
                <div className="mb-10">
                    <h2 className="text-2xl font-black uppercase tracking-tight text-gray-900">Modifier Sous-Catégorie</h2>
                    <p className="text-xs font-bold text-[#ffaa00]">ID sélectionné: {id}</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Parent Selection */}
                    <div className="space-y-3">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[#8A8A89] ml-2">Catégorie Parente</label>
                        <div className="relative">
                            <Layers className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <select
                                required
                                value={parentId}
                                onChange={(e) => setParentId(e.target.value)}
                                className="w-full pl-14 pr-10 py-5 bg-gray-50 border-none rounded-[1.5rem] focus:ring-2 focus:ring-[#ffaa00] outline-none font-bold text-sm appearance-none"
                            >
                                <option value="">Choisir la catégorie parente</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={String(cat.id)}>
                                        {cat.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Name Input */}
                    <div className="space-y-3">
                        <label className="text-[10px] font-black uppercase tracking-widest text-[#8A8A89] ml-2">Nom de la sous-catégorie</label>
                        <div className="relative">
                            <Type className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input
                                required
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full pl-14 pr-6 py-5 bg-gray-50 border-none rounded-[1.5rem] focus:ring-2 focus:ring-[#ffaa00] outline-none font-bold text-sm"
                            />
                        </div>
                    </div>

                    <button
                        disabled={saving}
                        type="submit"
                        className="w-full py-5 bg-[#ffaa00] text-white rounded-[1.5rem] font-black uppercase tracking-widest text-xs flex items-center justify-center gap-3 hover:bg-black transition-all shadow-lg disabled:opacity-50"
                    >
                        {saving ? <Loader2 className="animate-spin" /> : "Enregistrer les modifications"}
                    </button>
                </form>
            </div>
        </motion.div>
    );
};

export default EditSousCategory;